/**
 * @file index.tsx
 *
 * @fileoverview Renders the view for the job wizard step to add a new job.
 */
import Router from 'next/router';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { JobForm, JobFormProps } from '~/jobiqo/components/Job/Form';
import { JobCart } from '~/jobiqo/components/Job/Wizard/components/JobCart';
import JobWizardSteps from '~/jobiqo/components/Job/Wizard/components/JobWizardSteps';
import { JobWizardFlow } from '~/jobiqo/components/Job/Wizard/config';
import { WizardLayout } from '~/jobiqo/components/Wizard/Layout';
import { Job, Product } from '~/jobiqo/types/types';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { ScrollToElement } from '~/vcl/atoms/06-layout/11-scroll-to-element';
import { OneSidebarLayout } from '~/vcl/templates/01-layout/02-grid-layouts/02-one-sidebar';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { JobWizardCurrentOrg } from '~/jobiqo/components/Job/Wizard/components/JobWizardCurrentOrg';
import JobWizardSidebarSlot from '~/jobiqo/components/Slots/JobWizardSidebarSlot';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

interface JobWizardStepFormViewProps
  extends Pick<JobFormProps, 'jobType' | 'entity'> {
  errorList: React.ReactNode;
  fieldErrorList?: any[];
  onSubmit: (formData: any) => void;
  /**
   * If the wizard is in republishing mode.
   *
   * @default "new"
   */
  flow?: JobWizardFlow;
  /**
   * If the job is being submitted.
   *
   * @default "false"
   */
  submitting?: boolean;
  /**
   * The job passed to the form.
   */
  job?: Job;
  /**
   * The bundle information if the job is being posted with bundle.
   */
  bundle?: Product;
  /**
   * If its a job being republised.
   */
  republishing?: boolean;
}
export const JobWizardStepFormView = ({
  errorList,
  jobType,
  entity,
  onSubmit,
  flow = JobWizardFlow.New,
  submitting = false,
  job,
  fieldErrorList,
  bundle,
  republishing = false,
}: JobWizardStepFormViewProps) => {
  const { JOBS } = useFrontendConfig();
  const HIDE_JOB_CART = JOBS.HIDE_JOB_CART;

  return (
    <WizardLayout size="xLarge" Steps={<JobWizardSteps flow={flow} />}>
      <OneSidebarLayout
        sidebarPosition="right"
        sidebar={
          <>
            {!HIDE_JOB_CART ? (
              <JobCart flow={flow} {...(job ? { job } : null)} />
            ) : null}
            <JobWizardCurrentOrg />
            <JobWizardSidebarSlot />
          </>
        }
      >
        <div>
          {errorList && (
            <Main>
              <ScrollToElement>
                {errorList}
                <Spacing size="medium" />
              </ScrollToElement>
            </Main>
          )}
          <JobForm
            jobType={jobType}
            {...(entity ? { entity } : null)}
            bundle={bundle}
            saveLabel={<FormattedMessage id="next" defaultMessage="Next" />}
            onCancel={() => Router.back()}
            // Hide the organization info group if the user does not have a published organization profile.
            submittingJobMutation={submitting}
            onSubmit={onSubmit}
            fieldErrorList={fieldErrorList}
            republishing={true}
          />
        </div>
      </OneSidebarLayout>
    </WizardLayout>
  );
};
export default JobWizardStepFormView;
